import type { WizardStep } from './WizardStepper';

/**
 * Wizard step identifiers in order
 */
export const WIZARD_STEP_IDS = {
  PROFILE: 'profile',
  SOURCE: 'source',
  TARGET: 'target',
  TABLES: 'tables',
  QUIET_TIME: 'quietTime',
  DELETE_MODE: 'deleteMode',
  INITIAL_SYNC: 'initialSync',
} as const;

/**
 * Ordered list of all wizard steps
 */
const WIZARD_STEP_ORDER = [
  WIZARD_STEP_IDS.PROFILE,
  WIZARD_STEP_IDS.SOURCE,
  WIZARD_STEP_IDS.TARGET,
  WIZARD_STEP_IDS.TABLES,
  WIZARD_STEP_IDS.QUIET_TIME,
  WIZARD_STEP_IDS.DELETE_MODE,
  WIZARD_STEP_IDS.INITIAL_SYNC,
] as const;

export type WizardStepId = typeof WIZARD_STEP_ORDER[number];

/**
 * Configuration for overriding step completion status
 */
export type WizardStepCompletionOverrides = Record<string, boolean>;

/**
 * Generates wizard steps array with proper completed/current flags
 *
 * @param currentStepId - The ID of the current step
 * @param completionOverrides - Optional overrides for step completion status
 * @returns Array of wizard steps with proper state
 *
 * @example
 * // Simple usage - mark all steps before 'target' as completed
 * const steps = getWizardSteps('target');
 *
 * @example
 * // With completion overrides for dynamic checks
 * const steps = getWizardSteps('deleteMode', {
 *   quietTime: hasQuietTimeConfigured,
 *   deleteMode: hasDeleteModeConfigured,
 * });
 */
export function getWizardSteps(
  currentStepId: WizardStepId,
  completionOverrides?: WizardStepCompletionOverrides
): WizardStep[] {
  const currentIndex = WIZARD_STEP_ORDER.indexOf(currentStepId);

  return WIZARD_STEP_ORDER.map((stepId, index) => {
    const isCurrent = stepId === currentStepId;

    // By default, steps before the current step are completed
    const defaultCompleted = index < currentIndex;

    // Apply override if provided, otherwise use default
    const completed = completionOverrides?.[stepId] ?? defaultCompleted;

    return {
      id: stepId,
      completed,
      current: isCurrent,
    };
  });
}
